import { zodResolver } from '@hookform/resolvers/zod';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ExpenseCategory, inventoryCostsApi } from '../../services/inventoryCostsApi';

const schema = z.object({
  category: z.enum(['mechanical', 'cosmetics', 'maintenance', 'other']),
  subcategory: z.string().optional(),
  amount: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid dollar amount'),
  expense_date: z.string().min(1, 'Required'),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  vehicle: { id: string; label: string } | null;
  vehicleOptions?: Array<{ id: string; label: string }>;
}

const CATEGORY_OPTIONS: { value: ExpenseCategory; label: string; placeholder: string }[] = [
  { value: 'mechanical', label: 'Mechanical', placeholder: 'e.g., Brakes, Transmission' },
  { value: 'cosmetics', label: 'Cosmetics', placeholder: 'e.g., Paint hood, Upholstery' },
  { value: 'maintenance', label: 'Pre-Sale Maintenance', placeholder: 'e.g., Oil change, Tires' },
  { value: 'other', label: 'Other', placeholder: 'Describe briefly' },
];

const MAX_DROPDOWN_RESULTS = 10;

export const AddExpenseModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  vehicle,
  vehicleOptions,
}) => {
  const qc = useQueryClient();
  const [selectedVehicle, setSelectedVehicle] = useState<{ id: string; label: string } | null>(
    null
  );
  const [query, setQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      category: 'mechanical',
      expense_date: new Date().toISOString().slice(0, 10),
    },
  });

  // The effective vehicle used for API calls: provided prop takes precedence.
  const effectiveVehicle = vehicle ?? selectedVehicle;

  // Show picker only when no vehicle prop and vehicleOptions are provided.
  const showPicker = vehicle === null && Array.isArray(vehicleOptions);

  const filteredOptions = React.useMemo(() => {
    if (!vehicleOptions) return [];
    const q = query.trim().toLowerCase();
    if (!q) return vehicleOptions.slice(0, MAX_DROPDOWN_RESULTS);
    return vehicleOptions
      .filter((v) => v.label.toLowerCase().includes(q))
      .slice(0, MAX_DROPDOWN_RESULTS);
  }, [vehicleOptions, query]);

  const handleClose = () => {
    setSelectedVehicle(null);
    setQuery('');
    setDropdownOpen(false);
    onClose();
  };

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      if (!effectiveVehicle) throw new Error('Select a vehicle');
      return inventoryCostsApi.createExpense(effectiveVehicle.id, {
        category: values.category,
        subcategory: values.subcategory || undefined,
        amount: values.amount,
        expense_date: values.expense_date,
        notes: values.notes || undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-costs'] });
      qc.invalidateQueries({ queryKey: ['vehicle', effectiveVehicle?.id] });
      reset();
      setSelectedVehicle(null);
      setQuery('');
      setDropdownOpen(false);
      onSuccess?.();
      onClose();
    },
  });

  const selectedCategory = watch('category');
  const placeholder = CATEGORY_OPTIONS.find((o) => o.value === selectedCategory)?.placeholder;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Add Expense</h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-3">
          {/* Read-only vehicle label when passed directly (VehicleDetailPage path) */}
          {vehicle && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Vehicle</label>
              <p className="mt-1 text-sm text-gray-900">{vehicle.label}</p>
            </div>
          )}

          {/* Searchable vehicle picker (page-level path) */}
          {showPicker && (
            <div>
              <label htmlFor="vehicle-search" className="block text-sm font-medium text-gray-700">
                Vehicle
              </label>
              {selectedVehicle ? (
                <div className="mt-1 flex items-center justify-between rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
                  <span className="text-gray-900">{selectedVehicle.label}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedVehicle(null);
                      setQuery('');
                    }}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear vehicle selection"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="relative mt-1">
                  <input
                    id="vehicle-search"
                    type="text"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={dropdownOpen}
                    aria-controls="vehicle-listbox"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setDropdownOpen(true);
                    }}
                    onFocus={() => setDropdownOpen(true)}
                    onBlur={() => {
                      // Delay to allow option click to register before hiding
                      setTimeout(() => setDropdownOpen(false), 150);
                    }}
                    placeholder="Type year, make, model, or VIN…"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    autoComplete="off"
                  />
                  {dropdownOpen && filteredOptions.length > 0 && (
                    <ul
                      id="vehicle-listbox"
                      role="listbox"
                      aria-label="Vehicle options"
                      className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg text-sm"
                    >
                      {filteredOptions.map((opt) => (
                        <li
                          key={opt.id}
                          role="option"
                          aria-selected={false}
                          onMouseDown={() => {
                            setSelectedVehicle(opt);
                            setQuery('');
                            setDropdownOpen(false);
                          }}
                          className="cursor-pointer px-3 py-2 hover:bg-blue-50 hover:text-blue-700"
                        >
                          {opt.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          <fieldset>
            <legend className="block text-sm font-medium text-gray-700">Category</legend>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {CATEGORY_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center space-x-2 text-sm">
                  <input type="radio" value={opt.value} {...register('category')} />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <label htmlFor="subcategory" className="block text-sm font-medium text-gray-700">
              Subcategory (optional)
            </label>
            <input
              id="subcategory"
              type="text"
              placeholder={placeholder}
              {...register('subcategory')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
              Amount
            </label>
            <input
              id="amount"
              type="text"
              inputMode="decimal"
              {...register('amount')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {errors.amount && (
              <p role="alert" className="text-red-600 text-sm mt-1">
                {errors.amount.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="expense_date" className="block text-sm font-medium text-gray-700">
              Expense date
            </label>
            <input
              id="expense_date"
              type="date"
              {...register('expense_date')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {errors.expense_date && (
              <p role="alert" className="text-red-600 text-sm mt-1">
                {errors.expense_date.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
              Notes (optional)
            </label>
            <textarea
              id="notes"
              {...register('notes')}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          {mutation.isError && (
            <p role="alert" className="text-red-600 text-sm">
              {(mutation.error as Error).message || 'Failed to save expense'}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={handleClose} className="btn-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !effectiveVehicle}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddExpenseModal;
